"use client";

import * as React from "react";
import Link from "next/link";
import { Search } from "lucide-react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";

import { useDebounce } from "@/hooks/useDebounce";
import type { IArticle } from "@/lib/blog";
import { Dialog, DialogContent } from "@/shadcn/ui/dialog";
import { ScrollArea } from "@/shadcn/ui/scroll-area";
import { DialogTitle } from "@radix-ui/react-dialog";
import { getDescription, getTitle } from "@/lib/utils";
import { Divider } from "@/components/global/utils/Divider";

interface BlogSearchResponse {
  data: IArticle[];
  count: number;
  success: boolean;
  hasMore: boolean;
  currentPage: number;
  totalPages: number;
}

export function BlogSearch() {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const debounced = useDebounce(query, 350);

  const queryClient = useQueryClient();
  const limit = 3;

  // ref para o input (autofocus)
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K ou Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // -------------------------------------------------------------------------
  // 🔥 useInfiniteQuery (Load More)
  // -------------------------------------------------------------------------
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<BlogSearchResponse>({
    queryKey: ["blog-search", debounced],
    enabled: debounced.trim().length > 0,
    initialPageParam: 0, // offset inicial

    // pageParam = offset
    queryFn: async ({ pageParam }) => {
      const offset = pageParam;

      const params = new URLSearchParams({
        search: debounced.trim(),
        limit: String(limit),
        offset: String(offset),
      });

      const res = await fetch(`/api/blog/search?${params.toString()}`);

      if (!res.ok) throw new Error("Failed to fetch search");

      const json = await res.json();
      return json as BlogSearchResponse;
    },

    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined;

      const totalLoaded = allPages.reduce(
        (acc, page) => acc + page.data.length,
        0
      );

      if (totalLoaded >= lastPage.count) {
        return undefined;
      }

      return totalLoaded;
    },
  });

  const results = data?.pages.flatMap((page) => page.data) ?? [];

  React.useEffect(() => {
    if (!open) {
      setQuery("");
      queryClient.removeQueries({ queryKey: ["blog-search"] });
    }
  }, [open, queryClient]);

  return (
    <>
      {/* Trigger */}
      <button
        type="button"
        aria-label="Search"
        onClick={() => setOpen(true)}
        className="hidden lg:flex rounded-full bg-[#D8E8EF]/40 hover:bg-[#D8E8EF]/60 transition-colors h-[6rem] w-[6rem] justify-center items-center"
      >
        <Search className="h-[2.625rem] w-[2.625rem]" />
      </button>

      {/* mobile trigger */}
      <button
        type="button"
        aria-label="Search"
        onClick={() => setOpen(true)}
        className="w-full lg:hidden rounded-full bg-[#D8E8EF]/40 h-[6rem] flex justify-center items-center p-[.5rem] ps-[3rem]"
      >
        <input
          autoFocus
          ref={inputRef}
          placeholder="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full focus:outline-none text-black/80 bg-transparent"
        />
        <span className="h-full w-[6rem] rounded-full bg-white flex justify-center items-center">
          <Search className="h-[2rem] w-[2rem] min-h-[2rem] min-w-[2rem]" />
        </span>
      </button>

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="lg:max-w-[99.625rem] max-w-[95%] top-[50%] border-[.125rem] !py-0 !px-[4.38rem] border-black/10 bg-white rounded-[.75rem]">
          <DialogTitle className="sr-only">Search articles</DialogTitle>

          {/* Search bar */}
          <div className="flex items-center gap-[2.63rem]">
            <Search className="h-[2.625rem] w-[2.625rem]" />
            <input
              autoFocus
              ref={inputRef}
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="font-geist w-full h-[10.375rem] focus:outline-none text-black/40 text-base font-medium border-none bg-white"
            />
          </div>

          <Divider className="my-0" />

          {/* Results */}
          <div>
            {/* error */}
            {isError && (
              <p className="pt-2 text-xs text-red-400">Error searching.</p>
            )}

            {/* empty */}
            {!isLoading && debounced && results.length === 0 && (
              <p className="pt-2 text-xs text-muted-foreground/70 font-roboto">
                No articles found for “{debounced}”.
              </p>
            )}

            {/* list */}
            {!isLoading && results.length > 0 && (
              <ScrollArea className="pt-[5.5rem] max-h-[60vh] lg:h-[130rem] overflow-y-scroll scroll-hidden">
                <ul className="space-y-3 pb-2">
                  {results.map((article) => {
                    const slug = article.settings?.slug?.current;
                    const title = article.title ?? "Untitled";
                    const summary = article?.textContent;

                    return (
                      <li key={article._id}>
                        <Link
                          href={slug}
                          onClick={() => setOpen(false)}
                          className="flex flex-col gap-[2.12rem] transition-opacity hover:opacity-70 duration-500"
                        >
                          <h3 className="line-clamp-1 text-[3.5rem] font-normal font-helvetica tracking-[-0.105rem] text-black">
                            {getTitle({ text: title })}
                          </h3>

                          {summary && (
                            <p className="font-geist text-base text-black/80 font-medium tracking-[-0.06rem]">
                              {getDescription({ text: summary })}
                            </p>
                          )}
                        </Link>

                        <Divider className="my-[4.38rem]" />
                      </li>
                    );
                  })}
                </ul>

                {/* LOAD MORE */}
                {hasNextPage && (
                  <button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="w-full h-[8.125rem] px-[2rem] mb-[5.25rem] text-center text-base bg-transparent border-[.125rem] border-accent text-accent hover:bg-accent font-roboto uppercase hover:text-black transition"
                  >
                    {isFetchingNextPage ? "Loading..." : "LOAD MORE"}
                  </button>
                )}
              </ScrollArea>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
